import React, { useId } from "react";
import { Card, CardHeader, CardLabel } from "./ui/Card";
import { Num } from "./ui/Num";
import { Pill, PillGroup } from "./ui/Pill";
import {
  EMAIL_SEGMENTS,
  PUSH_SEGMENTS,
  segmentNeedsFilter,
  channelUsesEmail,
} from "./broadcastEnums";
import { errorFor } from "./broadcastDraft";

/**
 * The composer's audience card: who receives the thing being composed.
 *
 * ---------------------------------------------------------------------------
 * ONE AUDIENCE, BECAUSE THERE IS ONE CHANNEL
 * ---------------------------------------------------------------------------
 * This card used to own the channel pills as well, and to render both audience
 * blocks at once when the draft targeted both. The composer picks a single mode
 * up front now — see `ComposeModeSwitch` — so exactly one block is ever shown,
 * and the mode choice has moved to the top of the page where it belongs.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS CARD STILL EXISTS TO ENFORCE
 * ---------------------------------------------------------------------------
 * Popup and email do NOT share an audience list.
 *
 * `EMAIL_SEGMENTS` carries three entries that `PUSH_SEGMENTS` does not — all
 * contacts, app users, and not on the app. They exist because email reaches
 * people who never installed the app. Offering any of them for a popup builds a
 * broadcast aimed at people with no device to receive it: an empty send that
 * looks perfectly valid right up until it delivers to nobody.
 *
 * The two lists are therefore rendered from their own constants and are never
 * merged, concatenated or filtered into one another here. `broadcastEnums.js`
 * already draws the line; this file's job is not to erase it.
 *
 * ---------------------------------------------------------------------------
 * A FAILED ESTIMATE IS UNKNOWN, NOT ZERO
 * ---------------------------------------------------------------------------
 * When `failed` is set, every count and every reachability line disappears.
 * Degrading to `0` would tell an author their segment is empty, which is a
 * different — and far more actionable — statement than "we could not ask".
 * The previous build shipped exactly that bug: a 422 on /estimate rendered
 * "0 clients" for every segment, on the screen whose whole job is telling you
 * how many people you are about to message.
 *
 * Purely presentational: `estimate` arrives decoded, nothing here fetches.
 */

const HEADER_ROW = "flex items-baseline justify-between gap-3";
const REACH_LINE = "mt-2 text-xs text-bc-muted";

/* ── Pieces ───────────────────────────────────────────────────────────── */

/**
 * A field-level validation message.
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {string} [props.message] - From `errorFor(errors, field)`.
 * @returns {React.ReactElement|null}
 */
const FieldError = ({ id, message }) =>
  message ? (
    <div id={id} role="alert" className="mt-1.5 text-xs text-bc-bad">
      {message}
    </div>
  ) : null;

/**
 * The in-flight / broken indicator that sits beside the audience label.
 *
 * @param {Object} props
 * @param {boolean} [props.estimating] - A request is in flight.
 * @param {boolean} [props.failed] - The last request did not answer.
 * @returns {React.ReactElement|null}
 */
const EstimateStatus = ({ estimating, failed }) => {
  if (failed) {
    return (
      <span className="text-xs text-bc-warn">
        Couldn't estimate the audience.
      </span>
    );
  }
  if (estimating) {
    return <span className="text-xs text-bc-faint">Estimating…</span>;
  }
  return null;
};

/**
 * The audience block: label, total, segment pills, reachability line.
 *
 * The count on a pill is the honest part. The API estimates the SELECTED
 * segment and only that one, so every other pill is handed `count={null}` and
 * renders an em dash. Spreading the selected segment's figure across the rest
 * would be inventing data — six pills all claiming 8,412 users, five of them
 * fabricated — and an author would have no way to tell which one was measured.
 *
 * @param {Object} props
 * @param {string} props.label - Section label, e.g. "Popup audience". Also the radiogroup's accessible name.
 * @param {Array<{ id: string, label: string }>} props.segments - `PUSH_SEGMENTS` or `EMAIL_SEGMENTS`. Never a merge of the two.
 * @param {string} props.selected - The chosen segment id.
 * @param {(id: string) => void} props.onSelect - Called with the clicked segment id.
 * @param {Object|null} [props.channelEstimate] - The decoded per-channel estimate block, or null when the server sent none.
 * @param {boolean} [props.estimating]
 * @param {boolean} [props.failed]
 * @param {string} [props.countLabel] - Unit spoken after a pill's count.
 * @param {React.ReactNode} [props.children] - The reachability line, rendered under the pills.
 * @param {string} [props.testId]
 * @returns {React.ReactElement}
 */
const AudienceBlock = ({
  label,
  segments,
  selected,
  onSelect,
  channelEstimate,
  estimating,
  failed,
  countLabel,
  children,
  testId,
}) => {
  // Every number on this block is gated on the same flag, in one place, so a
  // future addition cannot leak a zero into a failed state by forgetting.
  const showsNumbers = !failed && Boolean(channelEstimate);

  return (
    <div className="mt-4" data-testid={testId}>
      <div className={HEADER_ROW}>
        <div className="flex items-baseline gap-2">
          <CardLabel>{label}</CardLabel>
          <EstimateStatus estimating={estimating} failed={failed} />
        </div>
        {showsNumbers ? (
          <div className="shrink-0 text-xs text-bc-muted">
            <Num value={channelEstimate.audienceCount} className="text-xs" />{" "}
            total
          </div>
        ) : null}
      </div>

      <PillGroup label={label} className="mt-2">
        {segments.map((segment) => (
          <Pill
            key={segment.id}
            selected={selected === segment.id}
            onSelect={() => onSelect(segment.id)}
            countLabel={countLabel}
            count={
              showsNumbers && selected === segment.id
                ? channelEstimate.audienceCount
                : null
            }
          >
            {segment.label}
          </Pill>
        ))}
      </PillGroup>

      {showsNumbers ? children : null}
    </div>
  );
};

/* ── The card ─────────────────────────────────────────────────────────── */

/**
 * The audience for the mode being composed.
 *
 * @param {Object} props
 * @param {Object} props.draft - Reads `channels`, and then only the matching segment field.
 * @param {(partial: Object) => void} props.onPatch - Merges a partial into the draft.
 * @param {Array<{ field: string, message: string }>} [props.errors] - The array from `validateDraft`.
 * @param {Object|null} [props.estimate] - Decoded estimate: `{ push, email, ... }`. The block for the active channel may still be null.
 * @param {boolean} [props.estimating=false] - A request is in flight.
 * @param {boolean} [props.failed=false] - The last request did not answer. Suppresses every number.
 * @param {React.ReactNode} [props.filterSlot] - The saved-filter picker for the active channel. A SLOT, not an import: `FilterPicker` fetches the saved filters, the field registry and the preset catalogue, and importing it here would drag that whole service chain into a card that is deliberately presentational. Same seam as `PushContentCard`'s `imageSlot`.
 * @param {string} [props.className] - Extra classes on the card, merged last.
 * @returns {React.ReactElement}
 */
export const AudienceCard = ({
  draft,
  onPatch,
  errors,
  estimate,
  estimating = false,
  failed = false,
  filterSlot = null,
  className = "",
}) => {
  const uid = useId();
  const isEmail = channelUsesEmail(draft?.channels);

  const patch = (partial) => {
    if (onPatch) onPatch(partial);
  };

  // One object per mode rather than a pair of branches through the JSX: the
  // two audiences differ in every particular — list, field, copy, unit — and
  // interleaving them is how the email-only segments leaked into push before.
  const config = isEmail
    ? {
        testId: "bc-email-audience",
        label: "Email audience",
        subtitle: "Email reaches contacts who never installed the app.",
        segments: EMAIL_SEGMENTS,
        segmentField: "emailSegment",
        filterField: "emailFilterId",
        countLabel: "contacts",
        channelEstimate: estimate?.email || null,
      }
    : {
        testId: "bc-push-audience",
        label: "Popup audience",
        subtitle: "Only people with the app installed can receive a popup.",
        segments: PUSH_SEGMENTS,
        segmentField: "pushSegment",
        filterField: "pushFilterId",
        countLabel: undefined,
        channelEstimate: estimate?.push || null,
      };

  const selected = draft?.[config.segmentField];
  const push = estimate?.push || null;
  const email = estimate?.email || null;

  return (
    <Card className={`p-5 ${className}`} as="section">
      <CardHeader title="Audience" subtitle={config.subtitle} />

      <AudienceBlock
        testId={config.testId}
        label={config.label}
        segments={config.segments}
        selected={selected}
        onSelect={(id) => patch({ [config.segmentField]: id })}
        channelEstimate={config.channelEstimate}
        estimating={estimating}
        failed={failed}
        countLabel={config.countLabel}
      >
        {isEmail ? (
          <div data-testid="bc-email-reach" className={REACH_LINE}>
            <Num value={email?.reachable} className="text-xs" /> are opted in and
            deliverable · <Num value={email?.suppressed} className="text-xs" />{" "}
            suppressed · <Num value={email?.noAddress} className="text-xs" /> no
            address.
          </div>
        ) : (
          <div data-testid="bc-push-reach" className={REACH_LINE}>
            <Num value={push?.reachable} className="text-xs" /> of{" "}
            <Num value={push?.audienceCount} className="text-xs" /> have a live
            device token and will receive the popup.
          </div>
        )}
      </AudienceBlock>

      <FieldError
        id={`${uid}-segment-error`}
        message={errorFor(errors, config.segmentField)}
      />

      {/* The saved-filter segment is the only one that carries an id, so it is
          the only one that reveals a picker. */}
      {segmentNeedsFilter(selected) ? (
        <>
          {filterSlot}
          <FieldError
            id={`${uid}-filter-error`}
            message={errorFor(errors, config.filterField)}
          />
        </>
      ) : null}
    </Card>
  );
};

export default AudienceCard;
